import { FormField } from "@components/forms/FormField";
import { cn } from "@lib/utils/cn";
import type { LucideIcon } from "lucide-react";
import { type Control, Controller } from "react-hook-form";

interface TextFieldProps {
  name: string;
  control: Control<any>;
  label?: string;
  icon?: LucideIcon;
  type?: "text" | "password" | "url" | "number" | "email" | "tel";
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  hint?: string;
  /** Maximum input length — when set, renders a `{value.length}/{maxLength}` counter bottom-right */
  maxLength?: number;
}

export function TextField({
  name,
  control,
  label,
  icon,
  type = "text",
  placeholder,
  required,
  disabled,
  error,
  hint,
  maxLength,
}: TextFieldProps) {
  return (
    <Controller
      name={name}
      control={control}
      rules={required ? { required: `${label} est requis` } : undefined}
      render={({ field, fieldState }) => (
        <FormField
          label={label}
          icon={icon}
          required={required}
          error={fieldState.error?.message ?? error}
          hint={hint}
        >
          <div className="relative">
            <input
              {...field}
              value={field.value ?? ""}
              type={type}
              placeholder={placeholder}
              disabled={disabled}
              maxLength={maxLength}
              className={cn(
                "input input-bordered w-full bg-base-200 border-base-300 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200",
                (fieldState.error || error) && "input-error",
              )}
            />
            {maxLength !== undefined && (
              <span className="pointer-events-none absolute right-3 bottom-2 text-xs text-[var(--ink-faint)]">
                {(field.value ?? "").length}/{maxLength}
              </span>
            )}
          </div>
        </FormField>
      )}
    />
  );
}
